<template>
  <div class="buy-equity-modal">
    <DiscountConfirm v-model:showDiscountConfirm="showDiscountConfirm" v-if="isDiscountAvailable" />

    <div :class="['main-card', isUS() && 'is-us']">
      <div class="main-card__content">
        <!-- 支付成功态 -->
        <div v-if="paySucceeded" class="pay-success pay-success--full-width">
          <PaySuccess :title="paySuccessTitle" :desc="paySuccessDesc" />
          <div class="pay-success__bar"></div>
          <PaySuccTip class="pay-success__tip" />
        </div>

        <!-- 支付中/待选择套餐态 -->
        <AbTest experiment="personal-rights-plan" v-else>
          <template #A>
            <PlanA
              v-model:selectedFee="selectedFee"
              v-model:currentRightsType="currentRightsType"
            />
          </template>
          <template #B>
            <PlanB
              v-model:selectedFee="selectedFee"
              v-model:currentRightsType="currentRightsType"
            />
          </template>
        </AbTest>
      </div>

      <template v-if="!isUS()">
        <!-- 分隔线，仅在未支付成功时展示右侧二维码面板 -->
        <div v-if="!paySucceeded" class="main-card__divider"></div>
        <!-- 右侧二维码面板：创建/轮询支付订单与咨询模块 -->
        <div v-if="order?.status === undefined && !isUS()" class="main-card__right-panel">
          <PayQrCode
            v-model:payType="currentPayType"
            v-model:agreementChecked="agreementAccepted"
            v-model:loading="isCreating"
            :buyType="selectedFee?.buy_type"
            :qrcode="qrCode"
            :reorder="create"
            :rightName="displayRightName"
            :behavior="ORDER_BEHAVIOR.BUY"
            :priceText="selectedFee?.price || 0"
            :expiredDate="displayedExpiredDateRange"
            :showDiscountCountdown="isDiscountAvailable"
            from="buy-credit"
          >
            <Consultant
              :rightType="currentRightType"
              :orderType="ORDER_TYPE.PACKAGE"
              @transfer="onTransferClick"
              :transferDisabled="false"
            />
          </PayQrCode>
        </div>
        <!-- 支付失败态（与上方条件成组，保持原逻辑不变） -->
        <PayFail v-else-if="!paySucceeded" @back="repay" />
      </template>
      <template v-else>
        <StripeOperation
          :url="raw?.vendor_redirect_url"
          :loading="isCreating"
          @payed="handlePaySucceeded"
        >
        </StripeOperation>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import AbTest from '@rendora/common/features/ab-test/index.vue';
import type { UseOrderInput } from '@rendora/common/features/payment/composables/types';
import useOrder from '@rendora/common/features/payment/composables/useOrder';
import { getI18nService } from '@rendora/common/services/i18n';
import { type IFees, useRightsStore } from '@rendora/common/store/rights';
import { useUserStore } from '@rendora/common/store/user';
import {
  BUY_TYPE,
  IRightsType,
  ORDER_BEHAVIOR,
  ORDER_TYPE,
  PAY_TYPE,
} from '@rendora/common/types/enums';
import { isUS } from '@rendora/common/utils/config';
import { formatPrice } from '@rendora/common/utils/transform';
import dayjs from 'dayjs';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { usePayment } from '../../store';
import Consultant from '../Consultant.vue';
import PayFail from '../PayFail.vue';
import PayQrCode from '../PayQrCode.vue';
import PaySuccess from '../PaySuccess.vue';
import PaySuccTip from '../PaySuccessTip.vue';
import StripeOperation from '../StripeOperation.vue';
import DiscountConfirm from './DiscountConfirm.vue';
import PlanA from './right-content/PlanA.vue';
import PlanB from './right-content/PlanB.vue';

const i18nService = getI18nService();
const $t = i18nService.t;

// 组件入参：默认权益类型（用于决定默认选中项）
const props = defineProps<{
  defaultRightsType: IRightsType;
}>();

// 本地状态
const showDiscountConfirm = ref(false);

// Store 初始化
const userStore = useUserStore();
const rightsStore = useRightsStore();
const paymentStore = usePayment();

// 响应式引用
const { rightsDisplay } = storeToRefs(rightsStore);
const { user } = storeToRefs(userStore);
const { isDiscountAvailable, discountRemainingTime } = storeToRefs(paymentStore);

// 当前选中的费用方案
const selectedFee = ref<IFees>();
const currentRightsType = ref<IRightsType>(props.defaultRightsType || IRightsType.PERSONAL);
const displayRightName = computed(() => {
  const circle = selectedFee.value?.days === 30 ? '月度' : '年度';
  const name =
    currentRightsType.value === IRightsType.PERSONAL_LITE
      ? personalLitePlanRights?.value?.rights_name
      : personalPlanRights?.value?.rights_name;
  return `${name}${circle}会员`;
});

// 支付成功的消息提示
const yuanFormat = computed(() => formatPrice(order.pay_fee || 0).string);
const paySuccessTitle = computed(() => yuanFormat.value);

const paySuccessDesc = computed(() => {
  const payAction = (() => {
    if (selectedFee.value?.buy_type === BUY_TYPE.PAYMENT) {
      return selectedFee.value.days === 30 ? $t('dan-yue') : $t('dan-nian');
    }
    return selectedFee.value?.days === 30 ? $t('lian-xu-bao-yue') : $t('lian-xu-bao-nian');
  })();

  switch (currentRightsType.value) {
    case IRightsType.PERSONAL_LITE:
      return `${personalLitePlanRights.value?.rights_name} ${payAction}`;
    case IRightsType.PERSONAL:
      return `${personalPlanRights.value?.rights_name} ${payAction}`;
    default:
      return '';
  }
});

// 下单参数（根据当前选中的费用方案实时计算）
const orderBody = computed<UseOrderInput>(() => ({
  rightsId: selectedFee.value?.id,
  timeIdentity: selectedFee.value?.time_identity,
  buyType: selectedFee.value?.buy_type ?? BUY_TYPE.PAYMENT,
  orderType: ORDER_TYPE.PACKAGE,
  rightsType: currentRightsType.value,
  behavior: ORDER_BEHAVIOR.BUY, // 目前统一按“购买新套餐”处理
}));

// 支付相关状态与方法
const {
  paySucceeded,
  currentPayType,
  agreementAccepted,
  isCreating,
  qrCode,
  order,
  currentRightType,
  create,
  repay,
  raw,
  setPayType,
} = useOrder(orderBody);

// 权益信息：个人标准版
const personalPlanRights = computed(() =>
  rightsDisplay.value?.rights.find(r => r.rights_type === IRightsType.PERSONAL),
);

// 权益信息：个人轻享版
const personalLitePlanRights = computed(() =>
  rightsDisplay.value?.rights.find(r => r.rights_type === IRightsType.PERSONAL_LITE),
);

// 展示文案：二维码区域的到期区间（与上方一致，这里保留独立计算以便后续差异化）
const displayedExpiredDateRange = computed(() => {
  let endDate = dayjs()
    .add(selectedFee.value?.days || 0, 'day')
    .format('YYYY.MM.DD');

  let beginDate = dayjs().format('YYYY.MM.DD');

  if (user.value!.personal_expired_time) {
    beginDate = dayjs(user.value!.personal_expired_time).format('YYYY.MM.DD');
    endDate = dayjs(user.value!.personal_expired_time)
      .add(selectedFee.value?.days || 0, 'day')
      .format('YYYY.MM.DD');
  }
  return `${beginDate}~${endDate}`;
});

onUnmounted(() => {
  paymentStore.updateBuyEquityModalActive(false);
});

onMounted(async () => {
  paymentStore.updateBuyEquityModalActive(true);

  if (discountRemainingTime.value <= 0) {
    showDiscountConfirm.value = true;
    paymentStore.startDiscountCountdown();
    return;
  }

  // 根据默认权益类型初始化默认选中项（优先选中订阅月付）
  switch (props.defaultRightsType) {
    case IRightsType.PERSONAL_LITE:
      selectedFee.value = personalLitePlanRights.value?.rights_by_time.find(
        v => v.buy_type === BUY_TYPE.PAYMENT && v.days === 365,
      );
      break;
    case IRightsType.PERSONAL:
      selectedFee.value = personalPlanRights.value?.rights_by_time.find(
        v => v.buy_type === BUY_TYPE.PAYMENT && v.days === 365,
      );
      break;
  }
});

watch(
  () => selectedFee.value,
  (newVal: IFees | undefined) => {
    if (newVal) {
      setPayType(newVal.buy_type === BUY_TYPE.SUBSCRIBE ? PAY_TYPE.ALIPAY : PAY_TYPE.WEIXIN);
    }
  },
);

// 转账拦截：主动触发重新下单（转为转账方式）
function onTransferClick() {
  create(true);
}

function handlePaySucceeded() {
  location.reload();
}
</script>

<style lang="scss" scoped>
.buy-equity-modal {
  width: 100%;
  height: 100%;
  background: var(--x-white-1);
  display: flex;
  border-radius: var(--x-radius-large);
}

.main-card__content {
  height: 620px;
  overflow: auto;
  scrollbar-gutter: stable;
}

.main-card {
  flex: 1 1 0%;
  display: flex;
  justify-content: space-between;
  background: var(--x-back-1);
  border-radius: var(--x-radius-large);
}

.main-card.is-us {
  flex-direction: column;
}

.main-card__divider {
  width: 1px;
  background: var(--x-back-3);
}

.main-card__right-panel {
  display: flex;
}

.pay-success {
  width: 100%;
}

.pay-success--full-width {
  width: 100%;
}

.pay-success__bar {
  width: 92%;
  margin: 0 auto;
  height: 1px;
  background: var(--x-back-3);
}

.pay-success__tip {
  margin-top: 28px;
  margin-bottom: 28px;
}
</style>
